import { intersection, union } from "lodash";
import { useUserProducts } from "./ProductHooks";
import { useLocSetting } from "./SettingsHooks";
import { useMemo } from "react";
import { getProductsBySkus } from "../utils/ProductUtils";
import { Product } from "../types/Product";
import apiService from "../services/apiService";
import { Language } from "../config/languages";

export enum AccessorySource {
  DIRECT = "DIRECT",
  RULE_DIRECT = "RULE_DIRECT",
  RULE_SPEC = "RULE_SPEC"
}

interface CockpitIdRef {
  _id: string;
}

type Repeater<T> = { value: T }[];

interface RawAccessoryRule {
  _id: string;
  name: string;
  accessories: CockpitIdRef[] | null;
  accessory_specifications: Repeater<{
    specification: CockpitIdRef;
    value: string;
  }> | null;
  target_specifications: Repeater<{
    specification: CockpitIdRef;
    value: string;
  }> | null;
  shared_specifications: Repeater<{
    accessory_specification: CockpitIdRef;
    target_specification: CockpitIdRef;
  }> | null;
}

interface SpecConst {
  key: string;
  value: string;
}

interface SharedSpec {
  accessoryKey: string;
  targetKey: string;
}

export interface AccessoryRule {
  _id: string;
  name: string;
  /** List of cockpit ids */
  accessories: string[];
  accessorySpecifications: SpecConst[];
  targetSpecifications: SpecConst[];
  sharedSpecifications: SharedSpec[];
}

type RawSpecifications = Record<string, CockpitIdRef & { key: string }>;

type AccessoryMatch = {
  id: string;
  source: AccessorySource[];
};

export function useAccessoryRules(): AccessoryRule[] {
  const accessoryRules = useLocSetting<RawAccessoryRule[]>("accessory_rules");
  const specifications = useLocSetting<RawSpecifications>("specifications");
  const rules = useMemo(() => {
    const rules: AccessoryRule[] = _getAccessoryRules(
      specifications,
      accessoryRules
    );
    //console.log("Accessory RULES", rules);
    return rules;
  }, [accessoryRules, specifications]);
  return rules;
}

export async function getAccessoryRules(
  lang: Language = "sv"
): Promise<AccessoryRule[]> {
  const settings = await apiService.loadSettings(lang);
  const accessoryRules: RawAccessoryRule[] = settings.accessory_rules;
  const specifications: RawSpecifications = settings.specifications;
  const rules: AccessoryRule[] = _getAccessoryRules(
    specifications,
    accessoryRules
  );
  return rules;
}

function _getAccessoryRules(
  specifications: RawSpecifications,
  accessoryRules: RawAccessoryRule[]
) {
  const rules: AccessoryRule[] = [];
  const specLookup: Record<string, string> = {};
  Object.values(specifications).forEach(
    ({ _id, key }) => (specLookup[_id] = key)
  );
  accessoryRules.forEach(rawRule => {
    const accessorySpecifications: SpecConst[] = (
      rawRule.accessory_specifications || []
    ).map(spec => {
      return {
        key: specLookup[spec.value.specification._id],
        value: spec.value.value
      };
    });
    const targetSpecifications: SpecConst[] = (
      rawRule.target_specifications || []
    ).map(spec => {
      return {
        key: specLookup[spec.value.specification._id],
        value: spec.value.value
      };
    });
    const sharedSpecifications: SharedSpec[] = (
      rawRule.shared_specifications || []
    ).map(spec => {
      return {
        accessoryKey: specLookup[spec.value.accessory_specification._id],
        targetKey: specLookup[spec.value.target_specification._id]
      };
    });
    rules.push({
      _id: rawRule._id,
      name: rawRule.name,
      accessories: (rawRule.accessories || []).map(a => a._id),
      accessorySpecifications,
      targetSpecifications,
      sharedSpecifications
    });
  });
  return rules;
}

function productMatchesSpecRule(prod: Product, rule: SpecConst): boolean {
  const matchValues = rule.value.split("|").map(v => v.trim());
  const prodValue = prod.specsIndexed?.[rule.key]?.value;
  //console.log('productMatchesSpecRule', matchValues, prodValue )
  return (
    prodValue !== undefined &&
    prodValue !== "" &&
    (rule.value === "*" || matchValues.includes(prodValue))
  );
}

export function getTargetsFor(
  rule: AccessoryRule,
  products: Product[]
): string[] {
  if (!rule?.targetSpecifications) return [];
  const targetIds = rule.targetSpecifications.reduce(
    (matches, specRule, idx) => {
      const _matches = products
        .filter(prod => productMatchesSpecRule(prod, specRule))
        .map(prod => prod._id);
      return idx > 0 ? intersection(matches, _matches) : _matches;
    },
    [] as string[]
  );
  //console.log('targetIds', targetIds)
  return targetIds;
}

function getMatchesFor(
  rules: AccessoryRule[],
  targetProduct: Product,
  products: Product[]
): AccessoryMatch[] {
  const accessories = rules.reduce((matches, rule) => {
    const prodsMatchingAccessorySpecRules = rule.accessorySpecifications.reduce(
      (matches, specRule, idx) => {
        const _matches = products
          .filter(prod => productMatchesSpecRule(prod, specRule))
          .map(prod => prod._id);
        return idx > 0 ? intersection(matches, _matches) : _matches;
      },
      [] as string[]
    );
    const prodsMatchingSharedSpecRules = rule.sharedSpecifications.reduce(
      (matches, specRule, idx) => {
        const _matches = products
          .filter(
            prod =>
              (prod.specsIndexed?.[specRule.accessoryKey]?.value ||
                "NOVALUE_A") ===
              (targetProduct.specsIndexed?.[specRule.targetKey]?.value ||
                "NOVALUE_T")
          )
          .map(prod => prod._id);
        return idx > 0 ? intersection(matches, _matches) : _matches;
      },
      [] as string[]
    );

    return [
      ...matches,
      ...rule.accessories.map(id => ({
        id,
        source: [AccessorySource.RULE_DIRECT]
      })),
      ...(rule.accessorySpecifications.length > 0 &&
      rule.sharedSpecifications.length > 0
        ? intersection(
            prodsMatchingAccessorySpecRules,
            prodsMatchingSharedSpecRules
          ).map(id => ({ id, source: [AccessorySource.RULE_SPEC] }))
        : union(
            prodsMatchingAccessorySpecRules,
            prodsMatchingSharedSpecRules
          ).map(id => ({ id, source: [AccessorySource.RULE_SPEC] })))
    ] as AccessoryMatch[];
  }, [] as AccessoryMatch[]);
  //console.log('accessoryMatches', accessoryMatches)
  return accessories;
}

function getMatchingTargetsFor(
  rules: AccessoryRule[],
  accProduct: Product,
  products: Product[]
): AccessoryMatch[] {
  const accessories = rules.reduce((matches, rule) => {
    const prodsMatchingTargetSpecRules = rule.targetSpecifications.reduce(
      (matches, specRule, idx) => {
        const _matches = products
          .filter(prod => productMatchesSpecRule(prod, specRule))
          .map(prod => prod._id);
        return idx > 0 ? intersection(matches, _matches) : _matches;
      },
      [] as string[]
    );
    const prodsMatchingSharedSpecRules = rule.sharedSpecifications.reduce(
      (matches, specRule, idx) => {
        const _matches = products
          .filter(
            prod =>
              (prod.specsIndexed?.[specRule.targetKey]?.value ||
                "NOVALUE_T") ===
              (accProduct.specsIndexed?.[specRule.accessoryKey]?.value ||
                "NOVALUE_A")
          )
          .map(prod => prod._id);
        return idx > 0 ? intersection(matches, _matches) : _matches;
      },
      [] as string[]
    );

    return [
      ...matches,
      ...(rule.accessorySpecifications.length > 0 &&
      rule.sharedSpecifications.length > 0
        ? intersection(
            prodsMatchingTargetSpecRules,
            prodsMatchingSharedSpecRules
          ).map(id => ({ id, source: [AccessorySource.RULE_SPEC] }))
        : union(prodsMatchingTargetSpecRules, prodsMatchingSharedSpecRules).map(
            id => ({ id, source: [AccessorySource.RULE_SPEC] })
          ))
    ] as AccessoryMatch[];
  }, [] as AccessoryMatch[]);
  //console.log('accessories', accessories)
  return accessories;
}

function _getAccessoriesForProduct(
  targetSku: string,
  rules: AccessoryRule[],
  products: Product[]
): AccessoryMatch[] {
  const targetProduct = products.find(p => p.sku === targetSku);
  if (!targetProduct) return [];

  const matchedAccessories = getMatchesFor(rules, targetProduct, products);

  //accessories defined in product collection:
  const productAccessories: AccessoryMatch[] = getProductsBySkus(
    products,
    targetProduct.accessories
  )
    .map(p => p._id)
    .map(id => ({ id, source: [AccessorySource.DIRECT] }));

  return [
    ...productAccessories.filter(
      pAcc => matchedAccessories.findIndex(mAcc => mAcc.id === pAcc.id) === -1
    ),
    ...matchedAccessories.map(mAcc => ({
      ...mAcc,
      source: [
        ...mAcc.source,
        ...(productAccessories.findIndex(pAcc => pAcc.id === mAcc.id) > -1
          ? [AccessorySource.DIRECT]
          : [])
      ]
    }))
  ];
}

export function useAccessoriesFor(targetSku: string): AccessoryMatch[] {
  const rules = useAccessoryRules();
  const products = useUserProducts();
  const targetProduct = products.find(p => p.sku === targetSku);
  if (!targetProduct) return [];

  const matchingRules = rules.filter(rule => {
    const isMatch = rule.targetSpecifications.reduce(
      (isMatch, specRule) =>
        isMatch && productMatchesSpecRule(targetProduct, specRule),
      true
    );
    return isMatch;
  });
  console.log(
    "useAccessoriesFor",
    targetSku,
    "matching rules",
    matchingRules.map(rule => rule.name)
  );

  return _getAccessoriesForProduct(targetSku, matchingRules, products);
}

export function getAccessoriesBySkuAndRule(
  targetSku: string,
  rule: AccessoryRule,
  products: Product[]
): AccessoryMatch[] {
  const targetProduct = products.find(p => p.sku === targetSku);
  if (!targetProduct) return [];

  return _getAccessoriesForProduct(targetSku, [rule], products);
}

export function useAccessoriesBySkuAndRule(
  targetSku: string,
  rule: AccessoryRule
): AccessoryMatch[] {
  const products = useUserProducts();
  return getAccessoriesBySkuAndRule(targetSku, rule, products);
}

export function useCartAccessories(targetSkus: string[]): AccessoryMatch[] {
  const rules = useAccessoryRules();
  const products = useUserProducts();

  const accessories = targetSkus.reduce((matches, targetSku) => {
    const targetProduct = products.find(p => p.sku === targetSku);
    if (!targetProduct) return [];

    const matchingRules = rules.filter(rule => {
      const isMatch = rule.targetSpecifications.reduce(
        (_isMatch, specRule) =>
          _isMatch && productMatchesSpecRule(targetProduct, specRule),
        true
      );
      return isMatch;
    });

    const matechesForTarget = _getAccessoriesForProduct(
      targetSku,
      matchingRules,
      products
    );

    return [
      ...matechesForTarget.filter(
        tAcc => matches.findIndex(mAcc => mAcc.id === tAcc.id) === -1
      ),
      ...matches
    ];
  }, [] as AccessoryMatch[]);
  console.log("useCartAccessories", targetSkus, accessories);
  return accessories;
}

export function useCompatibleWith(accessorySku: string): AccessoryMatch[] {
  const rules = useAccessoryRules();
  const products = useUserProducts();
  const accProduct = products.find(p => p.sku === accessorySku);
  if (!accProduct) return [];

  const matchingRules = rules.filter(rule => {
    const isMatch =
      (rule.accessorySpecifications.length > 0 &&
        rule.accessorySpecifications.reduce(
          (_isMatch, specRule) =>
            _isMatch && productMatchesSpecRule(accProduct, specRule),
          true
        )) ||
      rule.accessories.includes(accProduct._id);
    return isMatch;
  });
  console.log(
    "useCompatibleWith",
    accessorySku,
    "matching rules",
    matchingRules.map(rule => rule.name)
  );

  const matchedTargets = getMatchingTargetsFor(
    matchingRules,
    accProduct,
    products
  );

  //accessories defined in product collection:
  const productAccessoriesReversed = accProduct.accessoriesReversed
    ? getProductsBySkus(products, accProduct.accessoriesReversed)
        .map(p => p._id)
        .map(id => ({ id, source: [AccessorySource.DIRECT] }))
    : [];

  return [
    ...productAccessoriesReversed.filter(
      pAcc => matchedTargets.findIndex(mAcc => mAcc.id === pAcc.id) === -1
    ),
    ...matchedTargets.map(mAcc => ({
      ...mAcc,
      source: [
        ...mAcc.source,
        ...(productAccessoriesReversed.findIndex(pAcc => pAcc.id === mAcc.id) >
        -1
          ? [AccessorySource.DIRECT]
          : [])
      ]
    }))
  ];
}

export function useTargetsForAccessoryRule(rule: AccessoryRule): string[] {
  const products = useUserProducts();
  return getTargetsFor(rule, products);
}
